import type { TFunction } from 'i18next';
import {
  formatTranslatedOddLabelParts,
  translateBetMarketName,
} from 'helpers/betI18n';
import {
  buildTicketEventsPayload,
  verifyLiveQuotes,
  type LiveQuoteLeg,
} from 'services/api/slipService';
import type { LiveEventOdd } from 'types/LiveEvent';
import type { Odd } from 'types/Odd';
import type { SlipEntry } from 'types/SlipEntry';

export type LiveQuoteReviewRow = {
  key: string;
  label: string;
  before: string;
  after: string;
};

export function liveLegKey(
  eventId: string | number,
  oddCustomId: string | number
): string {
  return `${eventId}|${oddCustomId}`;
}

/** At least one enabled selection on the slip, live or prematch. */
export function hasEnabledSelection(entries: SlipEntry[]): boolean {
  return entries.some((e) => e.bets.some((b) => !b.disabled));
}

/** At least one enabled selection on a live event. */
export function hasLiveEnabledSelection(entries: SlipEntry[]): boolean {
  return entries.some((e) => e.isLive && e.bets.some((b) => !b.disabled));
}

/** The slip's live matches, for the price check while it is put together. */
export function liveEntries(entries: SlipEntry[]): SlipEntry[] {
  return entries.filter((e) => e.isLive);
}

export function captureLiveOddsSnapshot(
  entries: SlipEntry[]
): Map<string, number> {
  const m = new Map<string, number>();
  // Prematch prices are checked with the provider too, so all selections.
  for (const e of entries) {
    for (const b of e.bets) {
      if (b.disabled) continue;
      m.set(liveLegKey(e.match.fi, b.odd.custom_id), b.odd.odds);
    }
  }
  return m;
}

function plainOddPart(odd: Odd | LiveEventOdd, t: TFunction): string {
  const { name, handicap, header } = formatTranslatedOddLabelParts(odd, t);
  return [name, handicap, header].filter(Boolean).join(' ').trim();
}

/** Home — Away (or best available labels) for slip row context. */
export function slipEntryMatchTitle(entry: SlipEntry): string {
  const m = entry.match as {
    home?: { name?: string };
    away?: { name?: string };
    league?: string | { name?: string };
  };
  const home =
    m.home?.name ??
    (typeof m.league === 'string' ? m.league : m.league?.name) ??
    '';
  const away = m.away?.name ?? '';
  if (!home && !away) return '';
  if (!away) return home;
  if (!home) return away;
  return `${home} — ${away}`;
}

export function findBetOnSlip(
  entries: SlipEntry[],
  eventId: string | number,
  oddCustomId: string | number
): { entry: SlipEntry; betIndex: number } | null {
  const eid = String(eventId);
  const oid = String(oddCustomId);
  for (const entry of entries) {
    if (String(entry.match.fi) !== eid) continue;
    const betIndex = entry.bets.findIndex(
      (b) => String(b.odd.custom_id) === oid
    );
    if (betIndex >= 0) return { entry, betIndex };
  }
  return null;
}

/**
 * Picks the server says are no longer offered leave the slip. A prematch pick
 * whose match has kicked off stays, marked started and off the ticket, so the
 * player sees which match it was and can take it from live. Returns the new
 * slip and the match titles of each kind, for the toast.
 */
export function pruneUnavailableLiveLegsFromEntries(
  entries: SlipEntry[],
  legs: LiveQuoteLeg[]
): {
  nextEntries: SlipEntry[];
  removedTitles: string[];
  startedTitles: string[];
} {
  const bad = legs.filter((l) => !l.available);
  const removedTitles: string[] = [];
  const startedTitles: string[] = [];

  for (const leg of bad) {
    const titles = leg.started ? startedTitles : removedTitles;
    const found = findBetOnSlip(entries, leg.event_id, leg.odd_custom_id);
    if (found) {
      const title = slipEntryMatchTitle(found.entry);
      if (title && !titles.includes(title)) {
        titles.push(title);
      }
    } else if (leg.bet_name && !titles.includes(leg.bet_name)) {
      titles.push(leg.bet_name);
    }
  }

  const working: SlipEntry[] = entries.map((e) => ({
    ...e,
    bets: e.bets.map((b) => ({ ...b })),
  }));

  for (const leg of bad) {
    const mi = working.findIndex(
      (e) => String(e.match.fi) === String(leg.event_id)
    );
    if (mi < 0) continue;
    const bi = working[mi].bets.findIndex(
      (b) => String(b.odd.custom_id) === String(leg.odd_custom_id)
    );
    if (bi < 0) continue;
    const bet = working[mi].bets[bi];
    if (bet.timeout) {
      clearTimeout(bet.timeout);
    }
    if (leg.started) {
      working[mi].bets[bi] = {
        ...bet,
        disabled: true,
        started: true,
        timeout: undefined,
      };
      continue;
    }
    working[mi].bets.splice(bi, 1);
    if (working[mi].bets.length === 0) {
      working.splice(mi, 1);
    }
  }

  return { nextEntries: working, removedTitles, startedTitles };
}

/** The toast after pruning: which matches started, which picks went. */
export function prunedSlipMessage(
  t: TFunction,
  removedTitles: string[],
  startedTitles: string[]
): string {
  const parts: string[] = [];
  if (startedTitles.length > 0) {
    parts.push(
      t('prematch_started_on_slip_toast', {
        matches: startedTitles.join(', '),
      })
    );
  }
  if (removedTitles.length > 0) {
    parts.push(
      t('live_removed_from_slip_toast', { matches: removedTitles.join(', ') })
    );
  }
  return parts.length > 0
    ? parts.join(' ')
    : t('live_removed_from_slip_toast_generic');
}

/**
 * Marks prematch picks whose match has kicked off as started (and off the
 * ticket). Returns the same array when nothing changed.
 */
export function markKickedOffPicks(
  entries: SlipEntry[],
  now: number = Date.now()
): SlipEntry[] {
  let changed = false;
  const next = entries.map((e) => {
    const kickoff = Number((e.match as { time?: number }).time) * 1000;
    if (e.isLive || !(kickoff > 0) || kickoff > now) return e;
    if (e.bets.every((b) => b.isLive || b.started)) return e;
    changed = true;
    return {
      ...e,
      bets: e.bets.map((b) =>
        b.isLive || b.started ? b : { ...b, disabled: true, started: true }
      ),
    };
  });
  return changed ? next : entries;
}

export function legLabelForLiveQuote(
  entries: SlipEntry[],
  leg: LiveQuoteLeg,
  t: TFunction,
  opts?: { singleLine?: boolean }
): string {
  const found = findBetOnSlip(entries, leg.event_id, leg.odd_custom_id);
  if (!found) {
    return leg.bet_name || String(leg.odd_custom_id);
  }
  const { entry, betIndex } = found;
  const b = entry.bets[betIndex];
  const market = translateBetMarketName(b.bet, t);
  const oddPart = plainOddPart(b.odd, t);
  const selection = oddPart ? `${market} — ${oddPart}` : market;
  const matchLine = slipEntryMatchTitle(entry);
  if (matchLine) {
    if (opts?.singleLine) {
      return `${matchLine} · ${selection}`;
    }
    return `${matchLine}\n${selection}`;
  }
  return selection;
}

export function buildQuoteReviewRows(
  entries: SlipEntry[],
  legs: LiveQuoteLeg[],
  t: TFunction
): LiveQuoteReviewRow[] {
  return legs
    .filter((l) => l.changed && l.available && l.odds_server != null)
    .map((l) => ({
      key: liveLegKey(l.event_id, l.odd_custom_id),
      label: legLabelForLiveQuote(entries, l, t),
      before: (l.odds_client ?? 0).toFixed(2),
      after: l.odds_server!.toFixed(2),
    }));
}

export type LiveVerifyPhaseResult =
  | { kind: 'no_live' }
  | { kind: 'error'; toastKey: string }
  | { kind: 'quote_suspended' }
  | { kind: 'live_unavailable_prune'; legs: LiveQuoteLeg[] }
  | {
      kind: 'quote_review';
      snapshot: Map<string, number>;
      legs: LiveQuoteLeg[];
      rows: LiveQuoteReviewRow[];
    }
  | {
      kind: 'ready_submit';
      snapshot: Map<string, number>;
      legs: LiveQuoteLeg[];
    };

/**
 * First “Yes” on a slip: the server's quote check. With a live pick it runs for the
 * whole wait the player sees (8 s, see the API's bets/slip_window.go), from the click.
 *
 * Accept odds change ON (default) → submit at server prices without confirm popup.
 * Accept odds change OFF → show before/after confirm when odds_client ≠ odds_server.
 */
export async function runLiveQuoteVerifyPhase(
  entries: SlipEntry[],
  acceptedOddChanges: boolean,
  t: TFunction
): Promise<LiveVerifyPhaseResult> {
  if (!hasEnabledSelection(entries)) {
    return { kind: 'no_live' };
  }
  const snapshot = captureLiveOddsSnapshot(entries);
  const payload = buildTicketEventsPayload(entries);
  const verified = await verifyLiveQuotes(payload);
  if (verified === null) {
    return { kind: 'error', toastKey: 'network_error_message' };
  }
  const { legs, retryLeft } = verified;
  if (payload.length > 0 && legs.length === 0) {
    return { kind: 'error', toastKey: 'network_error_message' };
  }
  if (legs.some((l) => l.unconfirmed)) {
    return retryLeft > 0
      ? { kind: 'quote_suspended' }
      : { kind: 'error', toastKey: 'quotes_unconfirmed_message' };
  }
  if (legs.some((l) => !l.available)) {
    return { kind: 'live_unavailable_prune', legs };
  }
  const needsReview = !acceptedOddChanges && legs.some((l) => l.changed);
  if (needsReview) {
    return {
      kind: 'quote_review',
      snapshot,
      legs,
      rows: buildQuoteReviewRows(entries, legs, t),
    };
  }
  return { kind: 'ready_submit', snapshot, legs };
}

/**
 * The slip with the provider's prices put in for every pick that moved, or
 * null when none did. The ticket is written at those prices, so the slip must
 * show them too - with "accept odds changes" on, a moved price used to be
 * accepted unseen, and the slip's potential win could be a third of the one
 * the server refused as over the maximum.
 */
export function applyServerOddsToEntries(
  entries: SlipEntry[],
  legs: LiveQuoteLeg[]
): SlipEntry[] | null {
  const moved = legs.filter(
    (l) => l.available && l.changed && l.odds_server != null
  );
  if (moved.length === 0) return null;
  return entries.map((e) => ({
    ...e,
    bets: e.bets.map((b) => {
      const leg = moved.find(
        (l) =>
          String(l.event_id) === String(e.match.fi) &&
          String(l.odd_custom_id) === String(b.odd.custom_id)
      );
      return leg ? { ...b, odd: { ...b.odd, odds: leg.odds_server! } } : b;
    }),
  }));
}

/**
 * How much the slip's total odds moved at the provider: the product of
 * server / client price over the picks that changed (1 when none did).
 */
export function serverOddsRatio(legs: LiveQuoteLeg[]): number {
  return legs.reduce((ratio, l) => {
    if (!l.available || !l.changed || !l.odds_server || !l.odds_client) {
      return ratio;
    }
    return ratio * (l.odds_server / l.odds_client);
  }, 1);
}

/** Use last server prices as the baseline for `odds_client` on the next verify call. */
export function oddsClientOverridesFromVerifyLegs(
  legs: LiveQuoteLeg[]
): Map<string, number> {
  const m = new Map<string, number>();
  for (const l of legs) {
    if (!l.available || l.odds_server == null) continue;
    m.set(liveLegKey(l.event_id, l.odd_custom_id), l.odds_server);
  }
  return m;
}

export type LiveReviewConfirmResult =
  | { kind: 'error'; toastKey: string }
  | { kind: 'quote_suspended' }
  | { kind: 'live_unavailable_prune'; legs: LiveQuoteLeg[] }
  | {
      kind: 'quote_review_still';
      legs: LiveQuoteLeg[];
      rows: LiveQuoteReviewRow[];
    }
  | { kind: 'ready_submit'; legs: LiveQuoteLeg[] };

/**
 * Quote-review “Accept & submit”: the server's check again (a live ticket needs a fresh
 * one), using last server prices as baseline.
 */
export async function runLiveQuoteReviewConfirmPhase(
  entries: SlipEntry[],
  acceptedOddChanges: boolean,
  baselineLegs: LiveQuoteLeg[],
  t: TFunction
): Promise<LiveReviewConfirmResult> {
  const overrides = oddsClientOverridesFromVerifyLegs(baselineLegs);
  const payload = buildTicketEventsPayload(entries, overrides);
  const verified = await verifyLiveQuotes(payload);
  if (verified === null) {
    return { kind: 'error', toastKey: 'network_error_message' };
  }
  const { legs, retryLeft } = verified;
  if (legs.some((l) => l.unconfirmed)) {
    return retryLeft > 0
      ? { kind: 'quote_suspended' }
      : { kind: 'error', toastKey: 'quotes_unconfirmed_message' };
  }
  if (legs.some((l) => !l.available)) {
    return { kind: 'live_unavailable_prune', legs };
  }
  const needsReviewAgain = !acceptedOddChanges && legs.some((l) => l.changed);
  if (needsReviewAgain) {
    return {
      kind: 'quote_review_still',
      legs,
      rows: buildQuoteReviewRows(entries, legs, t),
    };
  }
  return { kind: 'ready_submit', legs };
}
